import React, { useContext } from 'react';
import { RequirementsProps } from '../../../tools/context';
import NightDayHeader from '../NightDayHeader';
import NightDayHeaderDay from '../NightDayHeaderDay';
import Articles from '../ResultArticles';
import { TravelPlanerResultsContext } from '../TravelPlanerResultsContext';
import TravelNightPoint from './TravelNightPoint';

const DEFAULT_IMAGE =
  'https://cdn.sanity.io/images/uxzn45t2/production/c97934a4a559be0c144f69e2b4bbc6a2527c89c7-1440x770.jpg';

interface Entry {
  key: string;
  value: any[];
}

interface Activity {
  id: string;
  name?: string;
  store_name?: string;
  city?: string;
  urlimg?: string;
  url?: string;
  description?: string;
}

interface TransformedActivity {
  id: string;
  title: string;
  place: string;
  image: string;
  fallbackImage: string;
  url: string;
  description: string;
  requirements: RequirementsProps;
  onClick: () => void;
}

export interface TravelDaysNightProps {
  requirements: RequirementsProps;
  dayIndex?: number;
  allData?: Record<string, Entry[]>;
  resultsInOneTab?: boolean;
}

const transformActivity = (
  activity: Activity,
  requirements: RequirementsProps,
  defaultImage: string
): TransformedActivity => ({
  id: activity.id,
  title: activity.name || activity.store_name || '',
  place: activity.city || '',
  image: activity.urlimg || '',
  fallbackImage: defaultImage,
  url: activity.url || '',
  description: activity.description || '',
  requirements,
  onClick: () => console.log('TODO: Show Chat with prefilled activity', activity),
});

const extractSpots = (data: Record<string, Entry[]> | undefined): Activity[] => {
  if (!data) return [];

  return Object.values(data).flatMap((dayEntries) =>
    dayEntries
      .filter((entry) => entry?.key.startsWith('night'))
      .flatMap((entry) => (Array.isArray(entry.value) ? entry.value : []))
  );
};

const extractActivities = (
  data: Record<string, Entry[]> | undefined,
  requirements: RequirementsProps,
  defaultImage: string
): TransformedActivity[] => {
  if (!data) return [];

  return Object.values(data).flatMap((dayEntries) =>
    dayEntries
      .filter((entry) => !entry?.key.startsWith('night'))
      .flatMap((entry) => (Array.isArray(entry.value) ? entry.value : []))
      .map((activity) => transformActivity(activity, requirements, defaultImage))
  );
};

export const TravelDaysNight: React.FC<TravelDaysNightProps> = ({
  dayIndex,
  requirements,
  allData,
  resultsInOneTab,
}) => {
  const { trip } = useContext(TravelPlanerResultsContext);
  const allSpots = extractSpots(allData);
  if (dayIndex === undefined && !resultsInOneTab) {
    return null;
  }
  const allActivities = extractActivities(allData, requirements, DEFAULT_IMAGE);

  const nightData = dayIndex ? trip[`JOUR ${dayIndex}`] || null : null;
  const activities =
    dayIndex && allData && dayIndex <= Object.values(allData).length
      ? (Object.values(allData)[dayIndex - 1] as Entry[])
          .filter((d) => !d?.key?.startsWith('night'))
          .flatMap((d) => d?.value ?? [])
          .map((activity) => transformActivity(activity, requirements, DEFAULT_IMAGE))
      : [];

  if (!nightData && !resultsInOneTab) return null;

  const activitiesShown = resultsInOneTab ? allActivities : activities;

  return (
    <div className="travelDaysNight">
      <NightDayHeader dayIndex={dayIndex} requirements={requirements} />
      {resultsInOneTab ? (
        allSpots.map((spot, index) => (
          <React.Fragment key={spot?.id || index}>
            <NightDayHeaderDay dayIndex={index + 1} />
            <TravelNightPoint nightData={spot} requirements={requirements} />
          </React.Fragment>
        ))
      ) : (
        <TravelNightPoint key={nightData.id} nightData={nightData} requirements={requirements} />
      )}
      <Articles activities={activitiesShown} requirements={requirements} />
    </div>
  );
};
